import React, { useState, useEffect } from 'react';
import Logo from './components/Logo';
import Dashboard from './components/Dashboard';
import UsersManagement from './components/UsersManagement';
import PartnersManagement from './components/PartnersManagement';
import OrdersManagement from './components/OrdersManagement';
import CustomOrdersManagement from './components/CustomOrdersManagement';
import ItemsManagement from './components/ItemsManagement';
import StoresManagement from './components/StoresManagement';
import PaymentRequestsManagement from './components/PaymentRequestsManagement';
import CategoriesManagement from './components/CategoriesManagement';
import BannerManagement from './components/BannerManagement';
import { adminAPI } from './services/api';
import './App.css';

type View = 'dashboard' | 'users' | 'partners' | 'orders' | 'customOrders' | 'items' | 'stores' | 'payments' | 'categories' | 'banners';

function App() {
  const [view, setView] = useState<View>('dashboard');
  const [stats, setStats] = useState<any>({});
  const [users, setUsers] = useState<any[]>([]);
  const [partners, setPartners] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [usersRes, partnersRes, ordersRes, itemsRes, storesRes] = await Promise.all([
        adminAPI.getAllUsers().catch(() => ({ data: [] })),
        adminAPI.getAllPartners().catch(() => ({ data: [] })),
        adminAPI.getAllOrders().catch(() => ({ data: [] })),
        adminAPI.getAllItems(),
        adminAPI.getAllStores().catch(() => ({ data: [] })),
      ]);

      setUsers(usersRes.data);
      setPartners(partnersRes.data);
      setOrders(ordersRes.data);
      setItems(itemsRes.data);
      setStores(storesRes.data);

      // Calculate stats
      const totalRevenue = ordersRes.data
        .filter((o: any) => o.status === 'Delivered')
        .reduce((sum: number, o: any) => sum + (o.totalAmount || 0), 0);

      setStats({
        totalUsers: usersRes.data.length,
        totalPartners: partnersRes.data.length,
        totalOrders: ordersRes.data.length,
        totalRevenue,
        activeOrders: ordersRes.data.filter((o: any) => 
          ['Pending', 'Confirmed', 'Preparing', 'Out for Delivery'].includes(o.status)
        ).length,
        totalItems: itemsRes.data.length,
        totalStores: storesRes.data.length,
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  return (
    <div className="App">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="logo" style={{display: 'flex', alignItems: 'center', gap: '10px', padding: '20px'}}>
          <Logo size={45} showText={true} />
          <span style={{fontSize: '20px', fontWeight: 'bold', color: '#fff'}}>Admin</span>
        </div>
        
        <nav className="nav">
          <button 
            className={view === 'dashboard' ? 'active' : ''} 
            onClick={() => setView('dashboard')}
          >
            📊 Dashboard
          </button>
          <button 
            className={view === 'users' ? 'active' : ''} 
            onClick={() => setView('users')}
          >
            👥 Users ({users.length})
          </button>
          <button 
            className={view === 'partners' ? 'active' : ''} 
            onClick={() => setView('partners')}
          >
            🚴 Partners ({partners.length})
          </button>
          <button 
            className={view === 'orders' ? 'active' : ''} 
            onClick={() => setView('orders')}
          >
            📦 Orders ({orders.length})
          </button>
          <button 
            className={view === 'customOrders' ? 'active' : ''} 
            onClick={() => setView('customOrders')}
          >
            📱 Custom Orders
          </button>
          <button 
            className={view === 'items' ? 'active' : ''} 
            onClick={() => setView('items')}
          >
            🛒 Items ({items.length})
          </button>
          <button 
            className={view === 'stores' ? 'active' : ''} 
            onClick={() => setView('stores')}
          >
            🏪 Stores ({stores.length})
          </button>
          <button 
            className={view === 'categories' ? 'active' : ''} 
            onClick={() => setView('categories')}
          >
            📂 Categories
          </button>
          <button 
            className={view === 'banners' ? 'active' : ''} 
            onClick={() => setView('banners')}
          >
            🎨 Banners/Slides
          </button>
          <button 
            className={view === 'payments' ? 'active' : ''} 
            onClick={() => setView('payments')}
          >
            💳 Payments
          </button>
        </nav>

        <button className="refresh-btn" onClick={handleRefresh}>
          🔄 Refresh Data
        </button>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {loading && <div className="loading">Loading...</div>}
        
        {view === 'dashboard' && <Dashboard stats={stats} />}
        {view === 'users' && <UsersManagement users={users} onRefresh={handleRefresh} />}
        {view === 'partners' && <PartnersManagement partners={partners} onRefresh={handleRefresh} />}
        {view === 'orders' && <OrdersManagement orders={orders} onRefresh={handleRefresh} />}
        {view === 'customOrders' && <CustomOrdersManagement onRefresh={handleRefresh} />}
        {view === 'items' && <ItemsManagement items={items} onRefresh={handleRefresh} />}
        {view === 'stores' && <StoresManagement stores={stores} onRefresh={handleRefresh} />}
        {view === 'categories' && <CategoriesManagement onRefresh={handleRefresh} />}
        {view === 'banners' && <BannerManagement onRefresh={handleRefresh} />}
        {view === 'payments' && <PaymentRequestsManagement />}
      </div>
    </div>
  );
}

export default App;
